import { captureException } from '@sentry/react';
import { useRouteError, isRouteErrorResponse } from 'react-router-dom';

import {
  ForbiddenError,
  InternalServerError,
  LoadingError,
} from '@platform/core/errors';

import { ForbiddenErrorPage } from '../../../components/errors/forbidden-error-page';
import { InternalServerErrorPage } from '../../../components/errors/internal-server-error-page';
import { LoadingErrorPage } from '../../../components/errors/loading-error-page';

/**
 * Used for application level core authorization errors
 */
export const AuthorizationErrorElement = () => {
  const error = useRouteError();

  if (import.meta.env.MODE === 'development') {
    // eslint-disable-next-line no-console
    console.warn(
      'platform-client: Error was caught by AuthorizationErrorElement.',
      error,
    );
  }

  if (error instanceof ForbiddenError) {
    return <ForbiddenErrorPage error={error} />;
  }

  if (error instanceof InternalServerError) {
    captureException(error, {
      tags: { platform: 'auth-internal-server-error' },
    });
    return <InternalServerErrorPage error={error} />;
  }

  if (error instanceof LoadingError) {
    captureException(error, {
      tags: { platform: 'auth-loading-failed' },
    });
    return <LoadingErrorPage />;
  }

  if (isRouteErrorResponse(error)) {
    if (error.status >= 400 && error.status < 500) {
      return (
        <ForbiddenErrorPage
          error={
            new ForbiddenError({
              reason: 'ACCESS_DENIED',
            })
          }
        />
      );
    }

    if (error.status === 503) {
      captureException(error, {
        tags: { platform: 'auth-loading-failed' },
      });
      return <LoadingErrorPage />;
    }

    if (error.status >= 500) {
      captureException(error, {
        tags: { platform: 'auth-internal-server-error' },
      });
      return <InternalServerErrorPage />;
    }
  }

  throw error; // bubble-up the error
};
